import {
  Box,
  Checkbox,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material'
import {
  columnSizingFeature,
  createColumnHelper,
  rowSelectionFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type OnChangeFn,
  type RowSelectionState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useEffect, useMemo, useRef, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { TEST_IDS } from 'test-ids'
import colors from 'common/colors'

import { getVirtualPadding } from './table-utils'
import { type TrxStreamData } from './types'

const features = tableFeatures({ rowSelectionFeature, columnSizingFeature })

export const trxStreamColumnHelper = createColumnHelper<
  typeof features,
  TrxStreamData
>()

export type TrxStreamColumn = ColumnDef<typeof features, TrxStreamData, any>

export type TrxStreamPagination = { page: number; pageSize: number }

const SELECT_COLUMN_ID = 'select'
const SELECT_COLUMN_WIDTH = 56
// Matches the DataGrid cell padding used by the app's other admin tables.
const CELL_PADDING_X = '10px'
const BODY_CELL_PADDING_Y = '4px'
const CHECKBOX_PADDING = '4px'
const ESTIMATED_ROW_HEIGHT = 33
const ROW_OVERSCAN = 10
const LOADING_BAR_HEIGHT = 4
const TABLE_MAX_HEIGHT = '70vh'

// The theme's MuiTableCell override drops body right padding, which runs this
// table's ellipsized text into the next column, so data cells get the
// DataGrid's horizontal padding back. The select column deliberately avoids
// padding="checkbox": on a size="small" table MUI pins that cell to 24px
// with a selector more specific than sx, so its width can't be overridden.
function getCellLayout(columnId: string) {
  if (columnId === SELECT_COLUMN_ID) {
    return {
      padding: 'none',
      align: 'center',
      px: undefined,
      bodyPy: undefined,
    } as const
  }
  return {
    padding: 'normal',
    align: 'inherit',
    px: CELL_PADDING_X,
    bodyPy: BODY_CELL_PADDING_Y,
  } as const
}

type TrxStreamTableProps = {
  rows: TrxStreamData[]
  columns: TrxStreamColumn[]
  // Column that absorbs the table's leftover width instead of keeping its
  // own size, so the table fills its container without stretching the
  // checkbox column.
  growColumnId: string
  rowCount: number
  isLoading: boolean
  pagination: TrxStreamPagination
  pageSizeOptions: number[]
  onPaginationChange: (pagination: TrxStreamPagination) => void
  rowSelection: RowSelectionState
  onRowSelectionChange: OnChangeFn<RowSelectionState>
}

export default function TrxStreamTable({
  rows,
  columns,
  growColumnId,
  rowCount,
  isLoading,
  pagination,
  pageSizeOptions,
  onPaginationChange,
  rowSelection,
  onRowSelectionChange,
}: TrxStreamTableProps): ReactElement {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)

  const allColumns = useMemo<TrxStreamColumn[]>(
    () => [
      trxStreamColumnHelper.display({
        id: SELECT_COLUMN_ID,
        size: SELECT_COLUMN_WIDTH,
        header: ({ table }) => (
          <Checkbox
            size="small"
            sx={{ p: CHECKBOX_PADDING }}
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            disabled={table.getRowModel().rows.length === 0}
            onChange={table.getToggleAllRowsSelectedHandler()}
            slotProps={{ input: { 'aria-label': t('select_all') } }}
            data-testid={TEST_IDS.trxStreamSelectAllCheckbox}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            size="small"
            sx={{ p: CHECKBOX_PADDING }}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            slotProps={{ input: { 'aria-label': t('select_row') } }}
            data-testid={TEST_IDS.trxStreamRowCheckbox}
          />
        ),
      }),
      ...columns,
    ],
    [columns, t],
  )

  const table = useTable({
    features,
    columns: allColumns,
    data: rows,
    getRowId: (row) => row.id,
    state: { rowSelection },
    onRowSelectionChange,
  })

  const tableRows = table.getRowModel().rows
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    getItemKey: (index) => tableRows[index].id,
    overscan: ROW_OVERSCAN,
  })

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [rows])

  const virtualItems = virtualizer.getVirtualItems()
  const padding = getVirtualPadding(virtualItems, virtualizer.getTotalSize())
  const columnCount = allColumns.length

  return (
    <Box data-testid={TEST_IDS.trxStreamTable}>
      <Box sx={{ height: LOADING_BAR_HEIGHT }}>
        {isLoading && <LinearProgress />}
      </Box>
      <TableContainer ref={scrollRef} sx={{ maxHeight: TABLE_MAX_HEIGHT }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            minWidth: table.getTotalSize(),
          }}
        >
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const layout = getCellLayout(header.column.id)
                  return (
                    <TableCell
                      key={header.id}
                      padding={layout.padding}
                      align={layout.align}
                      sx={{
                        px: layout.px,
                        width:
                          header.column.id === growColumnId
                            ? undefined
                            : header.getSize(),
                        backgroundColor: colors.greyBackground,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <table.FlexRender header={header} />
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {tableRows.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">
                  {t('no_uploads')}
                </TableCell>
              </TableRow>
            )}
            {padding.top > 0 && (
              <TableRow aria-hidden>
                <TableCell
                  colSpan={columnCount}
                  sx={{ height: padding.top, p: 0, border: 0 }}
                />
              </TableRow>
            )}
            {virtualItems.map((virtualRow) => {
              const row = tableRows[virtualRow.index]
              return (
                <TableRow
                  key={row.id}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  hover
                  selected={row.getIsSelected()}
                >
                  {row.getAllCells().map((cell) => {
                    const layout = getCellLayout(cell.column.id)
                    return (
                      <TableCell
                        key={cell.id}
                        padding={layout.padding}
                        align={layout.align}
                        sx={{
                          px: layout.px,
                          py: layout.bodyPy,
                          fontWeight: 'fontWeightRegular',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <table.FlexRender cell={cell} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
            {padding.bottom > 0 && (
              <TableRow aria-hidden>
                <TableCell
                  colSpan={columnCount}
                  sx={{ height: padding.bottom, p: 0, border: 0 }}
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rowCount}
        page={pagination.page}
        rowsPerPage={pagination.pageSize}
        rowsPerPageOptions={pageSizeOptions}
        onPageChange={(_event, page) =>
          onPaginationChange({ ...pagination, page })
        }
        onRowsPerPageChange={(event) =>
          onPaginationChange({ page: 0, pageSize: Number(event.target.value) })
        }
        labelRowsPerPage={t('rows_per_page')}
        labelDisplayedRows={({ from, to, count }) =>
          t('pagination_displayed_rows', { from, to, count })
        }
        getItemAriaLabel={(type) =>
          type === 'next'
            ? t('pagination_next_page')
            : t('pagination_previous_page')
        }
        data-testid={TEST_IDS.trxStreamPagination}
      />
    </Box>
  )
}
